import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { BookIllustrationComponent } from '@lite-workspace/shared/blocks/illustration';
import { HlmSpinner } from '@lite-workspace/shared/helm-ui/spinner';
import { IS_MOBILE } from '@lite-workspace/utils';

/** Which document the editor opens: the caption names it. */
export type BootShutterDocument = 'album' | 'book';

export const BOOT_SHUTTER_CAPTION: Record<BootShutterDocument, string> = {
  album: 'Загружаем альбом',
  book: 'Загружаем книгу',
};

/**
 * The editor's opening shutter, drawn by the route veil while the editor route resolves and by the editor
 * itself until its first spread: one picture from the click to the editor, never a spinner first.
 * Fills its flex-column host; the host owns position, backdrop and fade.
 */
@Component({
  selector: 'hlm-boot-shutter',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BookIllustrationComponent, HlmSpinner],
  template: `
    @if (isMobile()) {
      <div class="grid flex-1 place-items-center">
        <hlm-spinner tone="acc" [size]="28" />
      </div>
    } @else {
      <div class="flex flex-1 flex-col items-center justify-center whitespace-nowrap [background-image:var(--glow-wait)]">
        <app-book-illustration variant="spread" [pending]="true" />
        <div class="mt-9 flex flex-col items-center gap-3">
          <span class="text-sm font-medium text-c2">{{ caption() }}…</span>
          <!-- Тяжёлый шаблон тянет подложку в несколько мегабайт — предупреждаем, что ожидание нормальное. -->
          <span class="text-sm text-c2">Это может занять несколько секунд</span>
        </div>
        <div data-boot-veil-bar class="relative mt-6.5 h-0.5 w-55 overflow-hidden rounded-[var(--r-pill)] bg-acc-soft">
          <div class="boot-veil-bar absolute inset-y-0 w-2/5 rounded-[var(--r-pill)] bg-acc"></div>
        </div>
      </div>
    }
  `,
  styles: `
    :host {
      display: contents;
    }

    .boot-veil-bar {
      animation: boot-veil-slide var(--t-loop) var(--ease-smooth) infinite;
    }

    /* Бегунок (w-2/5) входит из-за левого края дорожки и уходит за правый. */
    @keyframes boot-veil-slide {
      from {
        left: calc(-0.4 * var(--size-full));
      }
      to {
        left: var(--size-full);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .boot-veil-bar {
        animation: none;
      }
    }
  `,
})
export class HlmBootShutter {
  readonly document = input.required<BootShutterDocument>();

  protected readonly isMobile = inject(IS_MOBILE);
  protected readonly caption = computed(() => BOOT_SHUTTER_CAPTION[this.document()]);
}
